/**
 * ポータル画面の共通部品（モーダル・バッジ・タブ・日付整形・簡易Markdown表示）
 */
import React from 'react';
import { getApiBase, getToken } from '../../services/api';

/** 認証つきファイルURL（新しいタブで開く用。requireAuth は ?token= を受け付ける） */
export function fileUrl(path: string): string {
  return `${getApiBase()}${path}${path.includes('?') ? '&' : '?'}token=${encodeURIComponent(getToken() ?? '')}`;
}

export function fmtDateTime(s: string | null | undefined): string {
  if (!s) return '';
  // SQLite の datetime('now') はUTC。日本時間で表示する
  const d = new Date(s.includes('T') ? s : `${s.replace(' ', 'T')}Z`);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export function fmtDate(s: string | null | undefined): string {
  if (!s) return '';
  return s.slice(0, 10).replace(/-/g, '/');
}

export function fmtMinutes(min: number | null | undefined): string {
  if (min === null || min === undefined) return '';
  return `${Math.floor(min / 60)}:${String(min % 60).padStart(2, '0')}`;
}

export const Badge: React.FC<{ color?: string; bg?: string; children: React.ReactNode }> = ({ color = '#374151', bg = '#f3f4f6', children }) => (
  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, color, background: bg, whiteSpace: 'nowrap' }}>
    {children}
  </span>
);

const STATUS_COLORS: Record<string, { c: string; bg: string }> = {
  pending:  { c: '#b45309', bg: '#fef3c7' },
  officer:  { c: '#7c3aed', bg: '#ede9fe' },
  checking: { c: '#0f766e', bg: '#e6f4f1' },
  settled:  { c: '#15803d', bg: '#dcfce7' },
  approved: { c: '#15803d', bg: '#dcfce7' },
  rejected: { c: '#dc2626', bg: '#fee2e2' },
  withdrawn:{ c: '#6b7280', bg: '#f3f4f6' },
  deleted:  { c: '#6b7280', bg: '#f3f4f6' },
};

export const StatusBadge: React.FC<{ status: string; label: string }> = ({ status, label }) => {
  const s = STATUS_COLORS[status] ?? { c: '#374151', bg: '#f3f4f6' };
  return <Badge color={s.c} bg={s.bg}>{label}</Badge>;
};

export const Modal: React.FC<{ title: React.ReactNode; onClose: () => void; width?: number; children: React.ReactNode }> = ({ title, onClose, width = 640, children }) => (
  <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    onClick={onClose}>
    <div style={{ background: '#fff', borderRadius: 14, width, maxWidth: '96vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}
      onClick={e => e.stopPropagation()}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>{title}</div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 18, padding: 4, lineHeight: 1 }}>✕</button>
      </div>
      <div style={{ padding: 20, overflowY: 'auto' }}>{children}</div>
    </div>
  </div>
);

export const Tabs: React.FC<{ tabs: Array<{ key: string; label: React.ReactNode }>; active: string; onChange: (k: string) => void }> = ({ tabs, active, onChange }) => (
  <div style={{ display: 'flex', gap: 4, borderBottom: '1.5px solid #e5e7eb', marginBottom: 14 }}>
    {tabs.map(t => (
      <button key={t.key} onClick={() => onChange(t.key)}
        style={{
          padding: '9px 16px', border: 'none', background: 'transparent', cursor: 'pointer',
          fontSize: 13.5, fontWeight: active === t.key ? 700 : 500,
          color: active === t.key ? 'var(--primary, #0066CC)' : '#6b7280',
          borderBottom: active === t.key ? '2.5px solid var(--primary, #0066CC)' : '2.5px solid transparent',
          marginBottom: -1.5,
        }}>
        {t.label}
      </button>
    ))}
  </div>
);

export const fieldStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', border: '1.5px solid #D1D5DB', borderRadius: 8,
  fontSize: 14, outline: 'none', boxSizing: 'border-box', background: '#fff', color: '#111827',
};
export const labelStyle: React.CSSProperties = { fontSize: 13, color: '#374151', fontWeight: 600, display: 'block', marginBottom: 5 };

/** ごく簡易なMarkdown表示（見出し・箇条書き・太字・段落のみ。マニュアルWiki用） */
export const MarkdownView: React.FC<{ text: string }> = ({ text }) => {
  const lines = (text ?? '').split(/\r?\n/);
  const out: React.ReactNode[] = [];
  let list: string[] = [];
  const flushList = (key: number) => {
    if (list.length === 0) return;
    out.push(
      <ul key={`ul-${key}`} style={{ margin: '6px 0 10px', paddingLeft: 22 }}>
        {list.map((li, i) => <li key={i} style={{ margin: '3px 0', fontSize: 14, color: '#1f2937', lineHeight: 1.7 }}>{inline(li)}</li>)}
      </ul>
    );
    list = [];
  };
  const inline = (s: string): React.ReactNode => {
    const parts = s.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => p.startsWith('**') && p.endsWith('**')
      ? <strong key={i}>{p.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{p}</React.Fragment>);
  };
  lines.forEach((raw, i) => {
    const line = raw.trimEnd();
    if (/^\s*[-*]\s+/.test(line)) { list.push(line.replace(/^\s*[-*]\s+/, '')); return; }
    flushList(i);
    if (line.startsWith('### ')) out.push(<h4 key={i} style={{ margin: '14px 0 6px', fontSize: 14.5, color: '#111827' }}>{inline(line.slice(4))}</h4>);
    else if (line.startsWith('## ')) out.push(<h3 key={i} style={{ margin: '16px 0 6px', fontSize: 15.5, color: '#111827' }}>{inline(line.slice(3))}</h3>);
    else if (line.startsWith('# ')) out.push(<h2 key={i} style={{ margin: '4px 0 10px', fontSize: 18, color: '#111827' }}>{inline(line.slice(2))}</h2>);
    else if (line.trim() === '') out.push(<div key={i} style={{ height: 6 }} />);
    else out.push(<p key={i} style={{ margin: '2px 0', fontSize: 14, color: '#1f2937', lineHeight: 1.8 }}>{inline(line)}</p>);
  });
  flushList(lines.length + 1);
  return <div>{out}</div>;
};


/** 空状態（イラストつき）。素材はユーザー提供のイラスト素材集から */
export const EmptyState: React.FC<{ img: string; text: string; size?: number }> = ({ img, text, size = 150 }) => (
  <div style={{ textAlign: 'center', padding: '26px 10px' }}>
    <img src={img} alt="" style={{ width: size, maxWidth: '70%' }} />
    <div style={{ fontSize: 13.5, color: '#50545b', marginTop: 10, fontWeight: 600 }}>{text}</div>
  </div>
);
